import { useState } from 'react';
import { Users } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { ParentTimetableGrid } from '../components/ParentTimetableGrid';
import { ParentWeekControls } from '../components/ParentWeekControls';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Timetable, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/timetable`, which is the same server-side
 * week the child's own panel is drawn from, and draws it with the same
 * `ClassTimetable` — so a family and their child are never shown two versions
 * of Tuesday. Nothing is added that the office has not recorded: no room, no
 * sign-off, no invented notes.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentTimetablePage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  /** Any day in the week on screen; empty is the week we are in. */
  const [weekOf, setWeekOf] = useState('');

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.timetable(child.uuid, weekOf || undefined) : Promise.resolve(null)),
    [child?.uuid, weekOf],
  );

  // Switching children refetches; until the new week lands, the old one belongs
  // to somebody else and must not be drawn under this child's name.
  const timetable = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's class timetable appears here."
        />
      </div>
    );
  }

  const upcoming = timetable && !timetable.timetable ? timetable.upcoming : null;

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-4')}>
      {/* A dated timetable that starts later would otherwise read as a class
          with no lessons at all. */}
      {timetable && !timetable.timetable && timetable.section && (
        <Alert tone="info" title="No timetable for this week">
          {upcoming ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>
                The next one applies from {formatDate(upcoming.effectiveFrom)}
                {upcoming.effectiveTo ? ` to ${formatDate(upcoming.effectiveTo)}` : ''}.
              </span>
              <Button variant="secondary" size="sm" onClick={() => setWeekOf(upcoming.effectiveFrom)}>
                Go to that week
              </Button>
            </span>
          ) : (
            'The school has not set a timetable for these dates yet.'
          )}
        </Alert>
      )}

      <ParentTimetableGrid
        weekControls={
          <ParentWeekControls
            weekStart={timetable?.weekStart}
            weekEnd={timetable?.weekEnd}
            pickedDate={weekOf}
            onChange={setWeekOf}
          />
        }
        timetable={timetable}
        isLoading={isLoading || (!timetable && !error)}
        error={error}
        onRetry={refetch}
        childName={child.fullName}
        className={child.className}
      />
    </div>
  );
}
